import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import FunnelLayout from "./FunnelLayout";
import "./CommonNewFunnel.css";

const LS_KEY = "glp1_intake_step9";

// ✅ API-ready question key
const Q9_KEY =
  "Q9_Please_select_the_following_options_that_you_are_interested_in";

const OPTIONS = [
  "Maintaining muscle mass as I lose weight",
  "Would prefer not to inject",
  "Managing potential side effects such as nausea/vomiting",
  "Assist with aging and longevity (cellular/DNA damage, immune system dysfunction, etc)",
  "Improving cognitive function and mental clarity",
  "Improving energy levels",
  "Regulating menses and hormonal status",
  "Improving sleep quality",
  "I'm not sure - I'd like to discuss formulation options with a clinician via a live virtual consult",
] as const;

type Option = (typeof OPTIONS)[number];

type FormState = {
  selected: Option[];
};

const DEFAULT: FormState = { selected: [] };

type OptionCardProps = {
  label: string;
  checked: boolean;
  onChange: () => void;
};

const OptionCard: React.FC<OptionCardProps> = ({ label, checked, onChange }) => {
  return (
    <button
      type="button"
      className={`funnel-option funnel-checkbox ${checked ? "is-selected" : ""}`}
      onClick={onChange}
    >
      <span className={`funnel-control checkbox ${checked ? "is-selected" : ""}`}>
        <span className="tick" />
      </span>

      <span className="funnel-option__label">{label}</span>

      <input
        className="sr-only"
        type="checkbox"
        checked={checked}
        onChange={onChange}
      />
    </button>
  );
};

type StoredPayload = {
  [Q9_KEY]?: string[];
};

const sanitizeSelected = (arr: unknown): Option[] => {
  if (!Array.isArray(arr)) return [];
  const set = new Set(OPTIONS as readonly string[]);
  return arr
    .filter((x): x is string => typeof x === "string" && set.has(x))
    .map((x) => x as Option);
};

const readFromLS = (): FormState => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return DEFAULT;

    const parsed = JSON.parse(raw) as StoredPayload;
    const selected = sanitizeSelected(parsed?.[Q9_KEY]);

    return { selected };
  } catch {
    return DEFAULT;
  }
};

const NewFunnelStep9: React.FC = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(DEFAULT);

  // ✅ prevents saving DEFAULT before we load saved state
  const [hydrated, setHydrated] = useState(false);

  // ✅ LOAD once
  useEffect(() => {
    setForm(readFromLS());
    setHydrated(true);
  }, []);

  // ✅ SAVE only after hydration
  useEffect(() => {
    if (!hydrated) return;

    // optional: if empty, remove LS instead of saving empty
    // if (form.selected.length === 0) {
    //   localStorage.removeItem(LS_KEY);
    //   return;
    // }

    const payload: StoredPayload = {
      [Q9_KEY]: form.selected,
    };

    localStorage.setItem(LS_KEY, JSON.stringify(payload));
  }, [form, hydrated]);

  const toggle = (val: Option) => {
    setForm((p) => ({
      ...p,
      selected: p.selected.includes(val)
        ? p.selected.filter((x) => x !== val)
        : [...p.selected, val],
    }));
  };

  const canGoNext = form.selected.length > 0;

  const next = () => {
    if (!canGoNext) return;

    // ✅ force save before navigation (extra-safe)
    localStorage.setItem(
      LS_KEY,
      JSON.stringify({
        [Q9_KEY]: form.selected,
      })
    );

    navigate("/glp1/intake-form/step-9");
  };

  return (
    <FunnelLayout>
      <h1 className="title-step-1 funnel-q-title-heading" style={{ color: "#000" }}>
        Your needs are <span>unique</span>, and your medicine should be, too!
      </h1>

      <p className="subtitle-step-1">
        Your GLP-1 medication is personalized to your specific needs
      </p>

      <div className="funnel-section">
        <div className="funnel-q-title">
          Please select the following options that you are interested in
        </div>

        {OPTIONS.map((v) => (
          <OptionCard
            key={v}
            label={v}
            checked={form.selected.includes(v)}
            onChange={() => toggle(v)}
          />
        ))}
      </div>

      <button className="next-btn" onClick={next} disabled={!canGoNext}>
        Next →
      </button>
    </FunnelLayout>
  );
};

export default NewFunnelStep9;